import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Collapse, Empty, Grid, Modal, Skeleton, Tabs } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ArrowLeftOutlined, CheckCircleOutlined, CheckOutlined } from '@ant-design/icons';

import { useGetCandidate } from '../../common/useGetCandidate';
import { DetailsPageHeader } from '../../components/DetailsPageHeader';
import { pathToUrl } from '../../utils/router';
import { PageRoutesEnum } from '../../utils/routes';
import { formatParsedCvExtractedAt } from '../candidate-ai/candidateAiParsedCv';
import { formatCount, splitSuggestionsByStatus } from '../candidate-ai/candidateAiSuggestions.formatters';
import { CandidateAiReviewAction } from '../candidate-ai/useCandidateAiReviewActions';
import { useGetCandidateAiParsedCv } from '../candidate-ai/useGetCandidateAiParsedCv';
import { useGetCandidateAiReviewState } from '../candidate-ai/useGetCandidateAiReviewState';
import { MatchingPageLayout } from '../candidate-project-matching/matching.styles';
import { candidateName } from '../candidate-project-matching/matchingPresentation';
import { CandidateType } from '../candidates/Candidates.types';

import { CandidateAiParsedCvPanel } from './CandidateAiParsedCvPanel';
import { candidateEditOpenConsentsNavigationState } from './candidateAiRefreshNavigation';
import { CandidateAiRejectedSuggestions } from './CandidateAiRejectedSuggestions';
import { CandidateAiScalarRows } from './CandidateAiScalarRows';
import { CandidateAiSetSuggestionGroup, SuggestionSection } from './CandidateAiSetSuggestionGroup';
import { getCvSuggestionsState } from './cvSuggestionsStatus';
import { useCandidateAiRefreshAfterCvUpload } from './useCandidateAiRefreshAfterCvUpload';
import { useCandidateAiReviewStateSource } from './useCandidateAiReviewStateSource';
import { GLOBAL_ACCEPT_ALL_KEY, useCandidateCvSuggestionsReview } from './useCandidateCvSuggestionsReview';
import { useStartCandidateAiRefreshForActiveProcessing } from './useStartCandidateAiRefreshForActiveProcessing';

const statusStyles = css`
  .cv-suggestions-status {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 12px;
    min-height: 20px;
  }
  .cv-suggestions-feedback {
    color: #166534;
    font-weight: 500;
  }
`;

// At xl the page becomes a fixed-height workspace: the header stays put and each pane scrolls on its own.
const PageLayout = styled(MatchingPageLayout)<{ $workspace: boolean }>`
  ${statusStyles}
  max-width: ${({ $workspace }) => ($workspace ? '1600px' : '1280px')};
  ${({ $workspace }) =>
    $workspace &&
    css`
      height: 100vh;
      min-height: 0;
      display: flex;
      flex-direction: column;
      padding-bottom: 0;
      overflow: hidden;

      .cv-suggestions-header {
        flex: none;
        padding-bottom: 16px;
        border-bottom: 1px solid #dce4ed;
      }
      .cv-suggestions-header .ant-alert:last-child {
        margin-bottom: 0;
      }
    `}
`;

/* minmax(0, …) stops a wide markdown table from widening its pane. */
const Columns = styled.div<{ $split: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 32px;
  align-items: start;

  ${({ $split }) =>
    $split &&
    css`
      flex: 1;
      min-height: 0;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 0;
      align-items: stretch;

      .cv-suggestions-pane {
        min-height: 0;
        padding: 20px 0 24px;
      }
      .cv-suggestions-pane:first-child {
        overflow-y: auto;
        padding-right: 28px;
        border-right: 1px solid #dce4ed;
      }
      .cv-suggestions-pane:last-child {
        display: flex;
        flex-direction: column;
        padding-left: 28px;
      }
      .cv-suggestions-pane:last-child > section {
        flex: 1;
        min-height: 0;
      }
    `}

  .cv-suggestions-cv-collapse {
    background: transparent;
  }
  .cv-suggestions-cv-collapse .ant-collapse-content-box {
    max-height: 70vh;
    display: flex;
    flex-direction: column;
  }
`;

const ACCEPT_GREEN = '#16a34a';

// Tonal green: it belongs to the accept family on the rows, without the weight of a primary button.
const AcceptAllButton = styled(Button)`
  &.ant-btn {
    height: 36px;
    padding: 0 16px;
    border-radius: 8px;
    font-weight: 500;
    color: #166534;
    border-color: rgba(34, 197, 94, 0.32);
    background: rgba(34, 197, 94, 0.08);
    box-shadow: none;
  }

  &.ant-btn:not(:disabled):hover {
    color: #14532d !important;
    border-color: rgba(34, 197, 94, 0.48) !important;
    background: rgba(34, 197, 94, 0.14) !important;
  }

  &.ant-btn:disabled {
    color: rgba(0, 0, 0, 0.25);
    border-color: #d9d9d9;
    background: rgba(0, 0, 0, 0.04);
  }
`;

const AcceptAllSummary = styled.div`
  margin-top: 4px;
  color: #526174;

  p {
    margin: 0 0 12px;
  }
  dl {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    gap: 6px 16px;
    margin: 0;
    padding: 12px 14px;
    background: #f7fafc;
    border: 1px solid #e5eaf0;
    border-radius: 8px;
  }
  dt {
    color: #64748b;
  }
  dd {
    margin: 0;
    color: #18283b;
    overflow-wrap: anywhere;
  }
`;

type SuggestionsTab = 'pending' | 'rejected';

const acceptAllPhrase = (count: number) =>
  count === 1 ? 'Accept 1 suggestion' : `Accept all ${formatCount(count, 'suggestion')}`;

const SuggestionTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 20px;
  }
  .ant-tabs-tab {
    font-size: 14px;
    font-weight: 500;
  }
  .cv-tab-count {
    margin-left: 2px;
    color: #64748b;
    font-variant-numeric: tabular-nums;
  }
`;

const matchingNote = 'Matching uses saved candidate data. Accept suggestions to include them.';

const ReviewNoticeAlert = ({
  notice,
}: {
  notice: ReturnType<typeof useCandidateCvSuggestionsReview>['reviewNotice'];
}) =>
  notice ? (
    <Alert
      showIcon
      type={notice.kind === 'conflict' || notice.kind === 'partial-success' ? 'warning' : 'error'}
      message={notice.message}
      description={
        notice.details?.length || notice.hiddenDetailsCount ? (
          <div>
            {notice.details?.map((detail) => (
              <div key={detail}>{detail}</div>
            ))}
            {!!notice.hiddenDetailsCount && <div>{`and ${notice.hiddenDetailsCount} more`}</div>}
          </div>
        ) : undefined
      }
    />
  ) : null;

const useCandidateWithLatestAiState = (id?: string) => {
  const candidateQuery = useGetCandidate(id);
  const { data: polledAiReviewState, refetch: refetchAiReviewState } = useGetCandidateAiReviewState(id);
  const { isAiSuggestionsProcessing: isAiRefreshSessionActive, startCandidateAiRefreshSession } =
    useCandidateAiRefreshAfterCvUpload({ aiReviewState: polledAiReviewState, refetchAiReviewState });
  const { effectiveAiReviewState, candidateWithLatestAiState } = useCandidateAiReviewStateSource({
    candidate: candidateQuery.data,
    polledAiReviewState,
    isAiRefreshSessionActive,
  });

  useStartCandidateAiRefreshForActiveProcessing({
    candidateId: id,
    isAiRefreshSessionActive,
    refetchAiReviewState,
    resolvedAiReviewState: effectiveAiReviewState,
    startCandidateAiRefreshSession,
  });

  const refresh = () => Promise.allSettled([candidateQuery.refetch(), refetchAiReviewState()]);

  return { candidateQuery, candidate: candidateWithLatestAiState, refresh };
};

export const CandidateCvSuggestionsPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isSplit = !!screens.xl;
  const candidatePath = pathToUrl(PageRoutesEnum.candidateEdit, { id });

  const { candidateQuery, candidate, refresh } = useCandidateWithLatestAiState(id);
  const pendingCount = useMemo(() => (candidate ? splitSuggestionsByStatus(candidate).pendingCount : 0), [candidate]);
  const { review, reviewNotice, isReviewLoading, activeReviewKey, activeReviewAction, clearedPending, feedback } =
    useCandidateCvSuggestionsReview({ candidate, pendingCount, refresh });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  return (
    <PageLayout $workspace={isSplit && !!candidate}>
      <Link className="matching-back" to={candidatePath}>
        <ArrowLeftOutlined /> Back to candidate
      </Link>
      {candidate ? (
        // Keyed so tab state never carries over to another candidate.
        <CvSuggestionsContent
          key={candidate.uuid}
          candidate={candidate}
          isSplit={isSplit}
          review={review}
          reviewNotice={reviewNotice}
          isReviewLoading={isReviewLoading}
          activeReviewKey={activeReviewKey}
          activeReviewAction={activeReviewAction}
          clearedPending={clearedPending}
          feedback={feedback}
          onGoToConsents={() => navigate(candidatePath, { state: candidateEditOpenConsentsNavigationState })}
        />
      ) : (
        <>
          <DetailsPageHeader titleLevel={1} title="CV suggestions" />
          {candidateQuery.isError ? (
            <Alert
              showIcon
              type="error"
              message="Could not load the candidate."
              action={
                <Button size="small" onClick={() => candidateQuery.refetch()}>
                  Try again
                </Button>
              }
            />
          ) : (
            <Skeleton active paragraph={{ rows: 8 }} />
          )}
        </>
      )}
    </PageLayout>
  );
};

type CvSuggestionsContentProps = {
  candidate: CandidateType;
  isSplit: boolean;
  onGoToConsents: () => void;
} & Pick<
  ReturnType<typeof useCandidateCvSuggestionsReview>,
  | 'review'
  | 'reviewNotice'
  | 'isReviewLoading'
  | 'activeReviewKey'
  | 'activeReviewAction'
  | 'clearedPending'
  | 'feedback'
>;

const CvSuggestionsContent: React.FC<CvSuggestionsContentProps> = ({
  candidate,
  isSplit,
  review,
  reviewNotice,
  isReviewLoading,
  activeReviewKey,
  activeReviewAction,
  clearedPending,
  feedback,
  onGoToConsents,
}) => {
  const split = useMemo(() => splitSuggestionsByStatus(candidate), [candidate]);
  const { pendingRows, pendingLists, rejectedRows, rejectedLists, pendingCount, rejectedCount } = split;
  const hasPending = pendingCount > 0;

  const { notice, isReadOnly, isRead } = getCvSuggestionsState(candidate, hasPending || rejectedCount > 0);
  const actionsDisabled = isReviewLoading || isReadOnly;

  // Shares the panel's query key, so the read date costs no extra request.
  const { data: parsedCv } = useGetCandidateAiParsedCv({
    candidateId: candidate.uuid,
    currentCvUuid: candidate.cv?.uuid ?? null,
    cvProcessing: candidate.cvProcessing,
    enabled: true,
  });
  const readAt = parsedCv?.basedOnCurrentCv ? formatParsedCvExtractedAt(parsedCv.parsedAt) : '';

  const statusLine = [
    hasPending && `${pendingCount} pending`,
    isRead && ['Read from CV', readAt].filter(Boolean).join(' · '),
  ]
    .filter(Boolean)
    .join(' · ');

  const globalAcceptActions = useMemo<CandidateAiReviewAction[]>(
    () => [
      ...pendingRows.map((row) => ({ field: row.field, type: 'ACCEPT_SCALAR' as const })),
      ...pendingLists.flatMap((list) =>
        list.items.map((item) => ({ field: list.field, type: 'ACCEPT_SET_ITEM' as const, value: item.value }))
      ),
    ],
    [pendingRows, pendingLists]
  );

  const sections = [
    { title: 'General', rows: pendingRows.filter((row) => row.section === 'General') },
    { title: 'Additional data', rows: pendingRows.filter((row) => row.section === 'Additional data') },
  ].filter((section) => section.rows.length > 0);

  const confirmAcceptAll = () => {
    const breakdown = [
      ...sections.map((section) => ({
        title: section.title,
        summary: section.rows.map((row) => row.label).join(', '),
      })),
      ...pendingLists.map((list) => ({
        title: list.label,
        summary:
          list.items.length === 1 ? list.items[0]?.displayValue : `${list.items.length} ${list.label.toLowerCase()}`,
      })),
    ];

    Modal.confirm({
      width: 460,
      icon: <CheckCircleOutlined style={{ color: ACCEPT_GREEN }} />,
      title: `${acceptAllPhrase(pendingCount)}?`,
      content: (
        <AcceptAllSummary>
          <p>{`The suggested values are saved to ${candidateName(candidate)}'s record.`}</p>
          <dl>
            {breakdown.map((entry) => (
              <React.Fragment key={entry.title}>
                <dt>{entry.title}</dt>
                <dd>{entry.summary}</dd>
              </React.Fragment>
            ))}
          </dl>
        </AcceptAllSummary>
      ),
      okText: pendingCount === 1 ? 'Accept suggestion' : `Accept ${formatCount(pendingCount, 'suggestion')}`,
      okButtonProps: { style: { background: ACCEPT_GREEN, borderColor: ACCEPT_GREEN, boxShadow: 'none' } },
      cancelText: 'Cancel',
      onOk: () => review(globalAcceptActions, GLOBAL_ACCEPT_ALL_KEY),
    });
  };

  const reviewProps = {
    actionsDisabled,
    isReviewLoading,
    activeReviewKey,
    activeReviewAction,
    onReview: review,
  };

  // The tab bar exists only while something is rejected; otherwise Pending is the whole pane.
  const [tab, setTab] = useState<SuggestionsTab>('pending');
  const activeTab: SuggestionsTab = rejectedCount > 0 ? tab : 'pending';
  const tabsRef = useRef<HTMLDivElement>(null);

  // Once nothing is rejected the bar goes away; when it returns it must open on Pending.
  useEffect(() => {
    if (rejectedCount === 0) setTab('pending');
  }, [rejectedCount]);

  const showRejected = () => {
    setTab('rejected');
    // The link lives in the panel being hidden, so hand focus to the Rejected tab.
    window.requestAnimationFrame(() =>
      tabsRef.current?.querySelector<HTMLElement>('[data-node-key="rejected"] [role="tab"]')?.focus()
    );
  };

  const pendingContent = hasPending ? (
    <>
      {sections.map((section) => (
        <SuggestionSection key={section.title} aria-label={section.title}>
          <div className="cv-suggestions-section-heading">
            <h2 className="cv-suggestions-section-title">{section.title}</h2>
          </div>
          <CandidateAiScalarRows rows={section.rows} {...reviewProps} />
        </SuggestionSection>
      ))}
      {pendingLists.map((list) => (
        <CandidateAiSetSuggestionGroup key={list.field} candidate={candidate} list={list} {...reviewProps} />
      ))}
    </>
  ) : (
    <div className="matching-empty">
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          rejectedCount > 0 ? (
            <>
              <span>Nothing pending</span>
              <div>
                <Button type="link" onClick={showRejected}>
                  {`View ${rejectedCount} rejected`}
                </Button>
              </div>
            </>
          ) : (
            'No suggestions to review'
          )
        }
      />
    </div>
  );

  // Remounted per candidate so keepPreviousData cannot carry one candidate's markdown into another's panel.
  const parsedCvPanel = <CandidateAiParsedCvPanel key={candidate.uuid} candidate={candidate} open />;

  return (
    <>
      <div className="cv-suggestions-header">
        <div className="matching-detail-header">
          <DetailsPageHeader
            titleLevel={1}
            title={`CV suggestions for ${candidateName(candidate)}`}
            actions={
              hasPending && (
                <AcceptAllButton
                  icon={<CheckOutlined aria-hidden />}
                  loading={isReviewLoading && activeReviewKey === GLOBAL_ACCEPT_ALL_KEY}
                  disabled={actionsDisabled}
                  onClick={confirmAcceptAll}
                >
                  {`${acceptAllPhrase(pendingCount)}…`}
                </AcceptAllButton>
              )
            }
          />
        </div>
        <div className="matching-muted cv-suggestions-status">
          {statusLine && <span>{statusLine}</span>}
          {/* Always mounted so screen readers announce each confirmation. */}
          <span role="status" aria-live="polite" className="cv-suggestions-feedback">
            {feedback && (
              <>
                <CheckOutlined aria-hidden /> {feedback}
              </>
            )}
          </span>
        </div>
        {hasPending && <div className="matching-muted">{matchingNote}</div>}
        <div style={{ marginTop: 24 }}>
          {notice && (
            <Alert
              showIcon
              type={notice.type}
              message={notice.message}
              description={notice.description}
              action={
                notice.offerConsents ? (
                  <Button size="small" onClick={onGoToConsents}>
                    Go to Consents
                  </Button>
                ) : undefined
              }
            />
          )}
          <ReviewNoticeAlert notice={reviewNotice} />
          {clearedPending && <Alert showIcon type="success" message="All CV suggestions reviewed." />}
        </div>
      </div>
      <Columns $split={isSplit} className={isSplit ? 'cv-suggestions-workspace' : undefined}>
        <section aria-label="Suggestions" className="cv-suggestions-pane">
          {rejectedCount > 0 ? (
            <div ref={tabsRef}>
              <SuggestionTabs
                size="small"
                activeKey={activeTab}
                onChange={(key) => setTab(key as SuggestionsTab)}
                items={[
                  {
                    key: 'pending',
                    label: (
                      <>
                        Pending <span className="cv-tab-count">{pendingCount}</span>
                      </>
                    ),
                    children: pendingContent,
                  },
                  {
                    key: 'rejected',
                    label: (
                      <>
                        Rejected <span className="cv-tab-count">{rejectedCount}</span>
                      </>
                    ),
                    children: (
                      <CandidateAiRejectedSuggestions
                        candidate={candidate}
                        rows={rejectedRows}
                        lists={rejectedLists}
                        {...reviewProps}
                      />
                    ),
                  },
                ]}
              />
            </div>
          ) : (
            pendingContent
          )}
        </section>
        {isSplit ? (
          <div className="cv-suggestions-pane">{parsedCvPanel}</div>
        ) : (
          <Collapse className="cv-suggestions-cv-collapse">
            {/* forceRender keeps the panel fetching while it is collapsed. */}
            <Collapse.Panel key="parsed-cv" header="Parsed CV" forceRender>
              {parsedCvPanel}
            </Collapse.Panel>
          </Collapse>
        )}
      </Columns>
    </>
  );
};
